/** Domain icon palette — PDA navy + teal theme */
export const DOMAIN = {
  records: { hue: '#0f2a4a', wash: '#e8eef4' },
  building: { hue: '#0d9488', wash: '#ccfbf1' },
} as const;

export const HERITAGE_IMAGES = [
  { src: '/assets/landing/heritage-ghat.jpg', altEn: 'Sangam ghat at Prayagraj', altHi: 'प्रयागराज संगम घाट' },
  { src: '/assets/landing/heritage-boats.jpg', altEn: 'Boats at Triveni Sangam', altHi: 'त्रिवेणी संगम पर नौकाएँ' },
  { src: '/assets/landing/heritage-fort.jpg', altEn: 'Allahabad Fort', altHi: 'इलाहाबाद किला' },
] as const;

export const PLATFORM_MODULES = [
  {
    key: 'dms' as const,
    domain: 'records' as const,
    en: 'DMS',
    hi: 'डी·एम·एस',
    badgeEn: 'Document Management',
    badgeHi: 'Document Management',
    descEn: 'Scan, index and search approved plan documents with OCR.',
    descHi: 'OCR के साथ स्वीकृत योजना दस्तावेज़ों का स्कैन, अनुक्रमण और खोज।',
    features: [
      { en: 'OCR indexing', hi: 'OCR अनुक्रमण' },
      { en: 'Full-text search', hi: 'पूर्ण-पाठ खोज' },
      { en: 'Plan documents', hi: 'योजना दस्तावेज़' },
    ],
  },
  {
    key: 'enforcement' as const,
    domain: 'building' as const,
    en: 'Enforcement',
    hi: 'अनुपालन',
    badgeEn: 'Building Compliance',
    badgeHi: 'भवन अनुपालन',
    descEn: 'LiDAR scans, compliance flags and officer review.',
    descHi: 'LiDAR स्कैन, अनुपालन फ्लैग और अधिकारी समीक्षा।',
    features: [
      { en: 'LiDAR scans', hi: 'LiDAR स्कैन' },
      { en: 'Compliance flags', hi: 'अनुपालन फ्लैग' },
      { en: 'Officer review', hi: 'अधिकारी समीक्षा' },
    ],
  },
] as const;

export const HOW_IT_WORKS = [
  {
    step: 1,
    icon: 'apply',
    en: 'Sign in',
    hi: 'साइन इन',
    subEn: 'Use your PDA officer credentials',
    subHi: 'अपने PDA अधिकारी क्रेडेंशियल का उपयोग करें',
  },
  {
    step: 2,
    icon: 'records',
    en: 'Choose module',
    hi: 'मॉड्यूल चुनें',
    subEn: 'Open DMS or Enforcement from the cards',
    subHi: 'कार्ड से डी·एम·एस या अनुपालन खोलें',
  },
  {
    step: 3,
    icon: 'building',
    en: 'Work in workspace',
    hi: 'कार्यक्षेत्र में काम करें',
    subEn: 'Search documents or review scans and flags',
    subHi: 'दस्तावेज़ खोजें या स्कैन व फ्लैग की समीक्षा करें',
  },
  {
    step: 4,
    icon: 'trackStep',
    en: 'Review & report',
    hi: 'समीक्षा और रिपोर्ट',
    subEn: 'Track decisions with audit-ready records',
    subHi: 'ऑडिट-तैयार अभिलेखों के साथ निर्णय ट्रैक करें',
  },
] as const;

export const ANNOUNCEMENTS = [
  {
    date: '2026-08-01',
    en: 'Smart building enforcement workspace is now available for PDA officers.',
    hi: 'PDA अधिकारियों के लिए स्मार्ट भवन अनुपालन कार्यक्षेत्र अब उपलब्ध है।',
  },
  {
    date: '2026-07-15',
    en: 'DMS OCR indexing enabled for approved building plan documents.',
    hi: 'स्वीकृत भवन योजना दस्तावेज़ों के लिए डी·एम·एस OCR अनुक्रमण सक्रिय।',
  },
  {
    date: '2026-07-04',
    en: 'Shared sign-in now connects DMS and Enforcement under one PDA portal.',
    hi: 'साझा साइन-इन अब डी·एम·एस और अनुपालन को एक PDA पोर्टल में जोड़ता है।',
  },
] as const;

export const LANDING_COPY = {
  skipLink: { en: 'Skip to main content', hi: 'मुख्य सामग्री पर जाएं' },
  screenReader: { en: 'Screen reader access', hi: 'स्क्रीन रीडर पहुंच' },
  highContrast: { en: 'High contrast', hi: 'उच्च कंट्रास्ट' },
  signIn: { en: 'Sign in', hi: 'साइन इन' },
  signOut: { en: 'Sign out', hi: 'साइन आउट' },
  portal: { en: 'Portal', hi: 'पोर्टल' },
  heroTitle: {
    en: 'One Platform for Documents & Building Compliance',
    hi: 'दस्तावेज़ और भवन अनुपालन के लिए एकीकृत डिजिटल मंच',
  },
  heroSub: {
    en: 'Integrated DMS and Smart Building Enforcement for PDA',
    hi: 'PDA के लिए एकीकृत डी·एम·एस और स्मार्ट भवन अनुपालन',
  },
  modulesCta: { en: 'Choose a module', hi: 'मॉड्यूल चुनें' },
  signInCta: { en: 'Officer sign in', hi: 'अधिकारी साइन इन' },
  locationTag: { en: 'Prayagraj · संगम नगरी', hi: 'प्रयागराज · संगम नगरी' },
  trustLine: {
    en: 'An initiative of Prayagraj Development Authority.',
    hi: 'प्रयागराज विकास प्राधिकरण की एक पहल।',
  },
  heritageTitle: {
    en: 'Serving the Sangam Nagri — bringing PDA document and building compliance services online for Prayagraj.',
    hi: 'संगम नगरी की सेवा में — प्रयागराज के लिए PDA की दस्तावेज़ व भवन अनुपालन सेवाएँ ऑनलाइन।',
  },
  heritageCredit: {
    en: 'Images: Wikimedia Commons (CC-licensed)',
    hi: 'छवियाँ: Wikimedia Commons (CC लाइसेंस)',
  },
  modulesTitle: { en: 'Platform Modules', hi: 'मंच मॉड्यूल' },
  modulesPickerSub: {
    en: 'Choose DMS or Enforcement to open the workspace',
    hi: 'कार्यक्षेत्र खोलने के लिए डी·एम·एस या अनुपालन चुनें',
  },
  modulesOpen: { en: 'Explore', hi: 'एक्सप्लोर करें' },
  howTitle: { en: 'How It Works', hi: 'कैसे काम करता है' },
  noticesTitle: { en: 'Announcements & Notices', hi: 'घोषणाएं एवं सूचनाएं' },
  footerQuick: { en: 'Quick links', hi: 'त्वरित लिंक' },
  footerGovLinks: { en: 'Government links', hi: 'सरकारी लिंक' },
  footerUpdated: { en: 'Last updated', hi: 'अंतिम अपडेट' },
  footerRights: {
    en: '© Prayagraj Development Authority. All rights reserved.',
    hi: '© प्रयागराज विकास प्राधिकरण। सर्वाधिकार सुरक्षित।',
  },
  upGov: { en: 'UP Government', hi: 'उ.प्र. सरकार' },
  indiaGov: { en: 'India.gov.in', hi: 'India.gov.in' },
} as const;

export const ORG = {
  fullName: {
    en: 'Prayagraj Development Authority',
    hi: 'प्रयागराज विकास प्राधिकरण',
  },
  shortName: 'PDA',
  contact: {
    address: {
      en: 'Civil Lines, Prayagraj, Uttar Pradesh 211001',
      hi: 'सिविल लाइन्स, प्रयागराज, उत्तर प्रदेश 211001',
    },
    phone: '+91-532-2420000',
    email: 'info@pdaonline.in',
    helpline: '1800-XXX-XXXX',
  },
} as const;
